<template>
  <el-dialog v-model="visible" class="small no-header no-padding auto-height" width="350px" center :title="bindType === 'phone' ? $t('Set Phone Number') : $t('Set Email')">
    <div class="el-message-box el-message-box--center" tabindex="-1">
      <div class="el-message-box__header show-close">
        <div class="el-message-box__title"><span>{{ bindType === 'phone' ? $t('Set Phone Number') : $t('Set Email') }}</span></div>
      </div>
      <div id="el-id-3013-18" class="el-message-box__content">
        <div class="el-message-box__container">
          <div class="el-message-box__message">
            <p>{{ bindType === 'phone' ? $t('Please enter the phone number and verification code.') : $t('Please enter the email and verification code.') }}</p>
            <el-form ref="refForm" class="form-container" :model="formData" :rules="formRules" onsubmit="return false" @keyup.enter="submitForm(refForm)">
              <el-form-item prop="account" class="form-item">
                <AutoInput class="form-input" v-model="formData.account" :placeholder="bindType === 'phone' ? $t('Please enter the phone number') : $t('Please enter your email')" />
              </el-form-item>
              <el-form-item prop="code" class="form-item form-item-code">
                <el-input
                  size="large"
                  :controls="false"
                  class="code-input"
                  :maxlength="4"
                  name="code"
                  ref="refCodeInput"
                  v-model="formData.code"
                  @keypress="handleKeyPress"
                  :placeholder="$t('Verification code')"
                />
                <button :loading="sendCodeLoading" type="text" size="small" class="send-btn" :disabled="countDownLastTime > 0" @click="handleSendCode(false)">{{ sendCodeBtnText }}</button>
              </el-form-item>
            </el-form>
          </div>
        </div>
      </div>
      <div class="el-message-box__btns">
        <el-button @click="visible = false">{{ $t('Cancel') }}</el-button>
        <el-button type="primary" @click="handleBindingAccount()" :disabled="btnDisabled" :loading="submitBtnLoading">{{ $t('Link') }}</el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script setup>
import { bindAccount, verifyCaptcha } from '@/api/user/user';
import AutoInput from '@/components/auto-input.vue';
import enums from '@/libs/enums';
import gtagTools from '@/libs/gtag-tools';
import { testValue } from '@/libs/tools';
import { getSessionStorage, postMessageToOpenerWindow, setSessionStorage } from '@/libs/utils';
import { removeCaptchaCache, sendCaptcha } from '@/services/login';
import { appStore, userStore } from '@/stores';
import { computed, getCurrentInstance, nextTick, reactive, ref } from 'vue';

const { $t } = getCurrentInstance().proxy

const emits = defineEmits(['on-success'])

const visible = ref(false)
// 账号类型：email / phone
const bindType = ref('')
// 是否是修改账号，false 为第一次绑定账号
let isChangeAccount = false

const refForm = ref(null)
// 显示绑定账号弹窗
const open = (type, isChange = false) => {
  bindType.value = type
  visible.value = true
  isChangeAccount = isChange
  nextTick(() => {
    refForm.value.resetFields()
  })
}

// 限制只能输入数字，number输入框还是可以输入字母E/e的
const handleKeyPress = e => {
  if (!/^\d$/.test(e.key)) {
    event.preventDefault()
    return false
  }
}

// 绑定账号表单
const formData = reactive({
  account: '',
  code: '',
  codeId: '',
})

const btnDisabled = computed(() => {
  return !formData.account || formData.code?.length < 4
})

const errorMsg = computed(() => bindType.value === 'email' ? $t('Please enter a valid email address') : $t('Please enter a valid mobile phone number'))
// 绑定账号验证
const formRules = reactive({
  account: [
    { required: true, message: () => errorMsg.value, trigger: 'blur' },
    {
      validator: (rule, value, callback) => {
        if (testValue(bindType.value, value)) {
          callback()
        } else {
          callback(new Error(errorMsg.value))
        }
      },
      trigger: 'change',
    },
  ],
  code: [{ required: true, message: $t('Please enter the verification code'), trigger: 'change' }],
})

// 发送验证码
const countDownLastTime = ref(0)
const refCodeInput = ref(null)
const countDownAllLastTime = appStore.isProd ? 60 : 20
const sendCodeLoading = ref(false)
const sendCodeBtnText = ref($t('Send code_2'))
let countDownTimer = null
// 记录缓存，用于刷新页面后再次弹窗时恢复倒计时
const countSendTimeKey = computed(() => `bind-account-send-time_${bindType.value}_${formData.account}`)
const codeIdKey = computed(() => `bind-account-code-id_${bindType.value}_${formData.account}`)
// 开始倒计时
const countDownTime = () => {
  if (countDownTimer) {
    clearInterval(countDownTimer)
    countDownTimer = null
  }
  sendCodeBtnText.value = countDownLastTime.value + 's'
  countDownTimer = setInterval(() => {
    countDownLastTime.value--
    if (countDownLastTime.value <= 0) {
      sendCodeBtnText.value = $t('Resend')
      clearInterval(countDownTimer)
      countDownTimer = null
      setSessionStorage(countSendTimeKey.value, '')
      setSessionStorage(codeIdKey.value, '')
    } else {
      sendCodeBtnText.value = countDownLastTime.value + 's'
    }
  }, 1000)
  nextTick(() => {
    refCodeInput.value.focus()
  })
}
const handleSendCode = async (isResend = false) => {
  if (!testValue(bindType.value, formData.account)) {
    refForm.value.validateField('account')
    return
  }
  if (sendCodeLoading.value && countDownLastTime.value === 0) return
  const cacheLastTime = parseInt((new Date().getTime() - parseInt(getSessionStorage(countSendTimeKey.value) || 0)) / 1000)
  const cacheCodeId = getSessionStorage(codeIdKey.value)
  // 如果距离上次发送时间小于60秒，则直接返回并开始倒计时
  if (cacheLastTime < countDownAllLastTime && cacheCodeId) {
    countDownLastTime.value = cacheLastTime
    formData.codeId = cacheCodeId
    countDownTime()
    return
  }
  sendCodeLoading.value = true
  try {
    const data = await sendCaptcha(formData.account, enums.sendCaptchaType.login, isResend)
    formData.codeId = data.codeId
    countDownLastTime.value = countDownAllLastTime
    setSessionStorage(countSendTimeKey.value, new Date().getTime())
    setSessionStorage(codeIdKey.value, data.codeId)
    countDownTime()
  } catch (e) {
    console.log(e)
  }
  sendCodeLoading.value = false
}

// 确认绑定账号
const submitBtnLoading = ref(false)
const handleBindingAccount = async () => {
  let gtagSetAccount = gtagTools.setPhone
  let gtagChangeAccount = gtagTools.changePhone
  if (bindType.value === 'email') {
    gtagSetAccount = gtagTools.setEmail
    gtagChangeAccount = gtagTools.changeEmail
  }
  refForm.value.validate(async valid => {
    if (valid) {
      if (submitBtnLoading.value) return
      submitBtnLoading.value = true
      let bindSuccess = false
      try {
        const checkCodeParams = {
          username: formData.account,
          type: enums.sendCaptchaType.login,
          codeId: formData.codeId,
          code: parseInt(formData.code)
        }
        await verifyCaptcha(checkCodeParams)
        const bindParams = {
          codeId: formData.codeId,
          code: parseInt(formData.code)
        }
        bindParams[bindType.value] = formData.account
        const { data } = await bindAccount(bindParams)
        // 清除验证码缓存
        removeCaptchaCache(formData.account, enums.sendCaptchaType.login)
        postMessageToOpenerWindow('userInfoUpdate')
        if (data.email) userStore.userInfo.email = data.email
        if (data.phone) userStore.userInfo.phone = data.phone
        bindSuccess = true
        visible.value = false
        emits('on-success', bindType.value)
      } catch (e) {
        console.log(e)
      }
      submitBtnLoading.value = false
      // 数据上报
      if (isChangeAccount) {
        gtagChangeAccount(bindSuccess)
      } else {
        gtagSetAccount(bindSuccess)
      }
    }
  })
}

defineExpose({
  open
})
</script>

<style lang="scss" scoped>
.el-message-box {
  overflow: unset;
}
.form-container {
  margin-top: 24px;
}

.el-message-box__btns {
  padding-top: 14px !important;
}

.el-message-box__message {
  width: 100%;
}

.form-item-code {
  position: relative;
  .code-input {
    width: 100%;
    :deep(.el-input__inner) {
      text-align: left;
    }
  }
  .send-btn {
    position: absolute;
    right: 6px;
    top: 6px;
    height: 24px;
    font-size: 12px;
    padding: 5px 8px;
    border-radius: 4px;
    border: none;
    color: var(--Color-1, #7479FF);
    background: none;
    cursor: pointer;
    &:hover:not(:disabled) {
      background: var(--BG-2, #EAEAEA);
    }
    &:disabled {
      cursor: default;
    }
  }
}
</style>